import {
  Card,
  CardActionArea,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate, Link as RouterLink, Routes, Route, Navigate, useLocation } from "react-router-dom";
import CreateJob from "./createJob";
import WebsiteBrandingPanel from "./WebsiteBrandingPanel";
import BillingPanel from "./BillingPanel";
import { DataGrid } from "@mui/x-data-grid";
import { glassPanel, glassPanelStrong, glassTextField } from "./glass";
import { getAuthHeaders, getRole, setRole, clearAuth } from "./auth";
import { useCatalogServices } from "./useCatalogServices";

function Dashboard() {
  const [ready, setReady] = useState(false);
  const [sessionRole, setSessionRole] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get("/user/settings", { headers: getAuthHeaders() })
      .then(({ data }) => {
        if (cancelled) return;
        const r = data.role === "superadmin" ? "superadmin" : data.role === "admin" ? "admin" : "customer";
        setRole(r);
        setSessionRole(r);
      })
      .catch(() => {
        if (!cancelled) {
          const r = getRole();
          setSessionRole(r === "superadmin" ? "superadmin" : r === "admin" ? "admin" : "customer");
        }
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (sessionRole === "superadmin") {
    return <Navigate to="/platform" replace />;
  }

  return <DashboardWithNav isAdmin={sessionRole === "admin"} />;
}

const ADMIN_TAB_PATHS = ["website", "jobs", "catalog", "billing", "account"];
const CUSTOMER_TAB_PATHS = ["account", "services"];

function dashboardSectionFromPath(pathname) {
  const rest = pathname.replace(/^\/dashboard\/?/, "").split("/")[0] || "";
  return rest;
}

function DashboardTopNav({ isAdmin }) {
  const location = useLocation();
  const raw = dashboardSectionFromPath(location.pathname);
  const validPaths = isAdmin ? ADMIN_TAB_PATHS : CUSTOMER_TAB_PATHS;
  const section = validPaths.includes(raw) ? raw : false;

  const tabs = isAdmin
    ? [
        { label: "Website", path: "website", hint: "Hero & embed" },
        { label: "Jobs", path: "jobs", hint: "Service jobs & notifications" },
        { label: "Catalog", path: "catalog", hint: "Quote page services" },
        { label: "Billing", path: "billing", hint: "Clibbins plan & Stripe" },
        { label: "Account", path: "account", hint: "Your profile" },
      ]
    : [
        { label: "Account", path: "account", hint: "Profile & contact" },
        { label: "My services", path: "services", hint: "Requests & status" },
      ];

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1190,
        ...glassPanelStrong,
        borderRadius: 0,
        borderBottom: "1px solid rgba(255,255,255,0.55)",
        boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        px: { xs: 0.5, sm: 1 },
        pt: 0.5,
        pb: 0,
      }}
    >
      <Box sx={{ maxWidth: 900, mx: "auto" }}>
        <Tabs
          value={section}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 48,
            "& .MuiTab-root": {
              minHeight: 48,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              letterSpacing: "-0.01em",
            },
            "& .Mui-selected": {
              color: "primary.main",
            },
          }}
        >
          {tabs.map((t) => (
            <Tab
              key={t.path}
              label={t.label}
              value={t.path}
              component={RouterLink}
              to={t.path}
              title={t.hint}
            />
          ))}
        </Tabs>
      </Box>
    </Box>
  );
}

function DashboardWithNav({ isAdmin }) {
  const defaultPath = isAdmin ? "website" : "account";

  return (
    <Box sx={{ minHeight: "100%", pt: 10 }}>
      <DashboardTopNav isAdmin={isAdmin} />
      <Box
        sx={{
          maxWidth: 900,
          mx: "auto",
          px: 2,
          pt: 3,
          pb: 4,
        }}
      >
        <Routes>
          <Route index element={<Navigate to={defaultPath} replace />} />
          {isAdmin ? (
            <>
              <Route
                path="website"
                element={
                  <>
                    <Typography variant="h5" fontWeight={700} letterSpacing="-0.02em" gutterBottom>
                      Website
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.5 }}>
                      Customize the hosted quote experience and add a button or iframe on your own marketing site.
                    </Typography>
                    <HomeHeroSettings />
                    <WebsiteBrandingPanel />
                  </>
                }
              />
              <Route path="jobs" element={<AdminJobsAndQuoting />} />
              <Route
                path="catalog"
                element={
                  <>
                    <Typography variant="h5" fontWeight={700} letterSpacing="-0.02em" gutterBottom>
                      Service catalog
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
                      What appears on the public quote page (<strong>/quote</strong>) and in the quote flow.
                    </Typography>
                    <CreateJob />
                  </>
                }
              />
              <Route
                path="billing"
                element={
                  <>
                    <Typography variant="h5" fontWeight={700} letterSpacing="-0.02em" gutterBottom>
                      Billing
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.5 }}>
                      Pay for Clibbins with Stripe, and optionally connect your own Stripe account to charge customers.
                    </Typography>
                    <BillingPanel />
                  </>
                }
              />
              <Route
                path="account"
                element={
                  <>
                    <Typography variant="h5" fontWeight={700} letterSpacing="-0.02em" gutterBottom>
                      Your account
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.5 }}>
                      Business contact details for your team record.
                    </Typography>
                    <AccountCard variant="admin" />
                  </>
                }
              />
            </>
          ) : (
            <>
              <Route
                path="account"
                element={
                  <>
                    <Typography variant="h5" fontWeight={700} letterSpacing="-0.02em" gutterBottom>
                      My lawn care
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.5 }}>
                      Your profile and how we reach you.
                    </Typography>
                    <AccountCard variant="customer" />
                  </>
                }
              />
              <Route path="services" element={<CustomerServicesSection />} />
            </>
          )}
          <Route path="*" element={<Navigate to={defaultPath} replace />} />
        </Routes>
      </Box>
    </Box>
  );
}

function CustomerServicesSection() {
  const [jobsRefresh, setJobsRefresh] = useState(0);
  return (
    <>
      <Typography variant="h5" fontWeight={700} letterSpacing="-0.02em" gutterBottom>
        My services
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.5 }}>
        Request work and track status — linked to the same catalog as the public quote page.
      </Typography>
      <RequestServiceForm onCreated={() => setJobsRefresh((n) => n + 1)} />
      <CustomerJobsGrid refreshKey={jobsRefresh} />
    </>
  );
}

export default Dashboard;

function LogOutButton() {
  const navigate = useNavigate();
  return (
    <Button
      variant="outlined"
      color="inherit"
      size="small"
      sx={{ mt: 1 }}
      onClick={() => {
        clearAuth();
        navigate("/", { replace: true });
      }}
    >
      Log out
    </Button>
  );
}

const JOBS_SUB_SERVICE = "service";
const JOBS_SUB_NOTIFICATIONS = "notifications";

const OUTBOUND_CHANNEL_OPTIONS = [
  { value: "none", label: "Off" },
  { value: "email", label: "Email only" },
  { value: "sms", label: "SMS only" },
  { value: "both", label: "Email & SMS" },
];

function ServiceJobsLeadSummary({ onConfigureNotifications, refreshKey }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get("/user/settings", { headers: getAuthHeaders() })
      .then(({ data }) => setEmail(data.quoteNotificationEmail || ""))
      .catch(() => setEmail(""))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return (
    <Box
      sx={{
        ...glassPanel,
        borderRadius: 2,
        p: 2,
        mb: 2,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Box sx={{ minWidth: 0, flex: "1 1 240px" }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Fast quote inbox
        </Typography>
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {email.trim() ? (
              <>
                Leads are emailed to <strong>{email}</strong>.
              </>
            ) : (
              <>No notification email set yet — configure it under Notifications.</>
            )}
          </Typography>
        )}
      </Box>
      <Button variant="outlined" size="small" onClick={onConfigureNotifications}>
        Configure notifications
      </Button>
    </Box>
  );
}

function OutboundNotificationChannels() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customerOutboundChannel, setCustomerOutboundChannel] = useState("email");
  const [crewOutboundChannel, setCrewOutboundChannel] = useState("email");
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    let cancelled = false;
    axios
      .get("/site-settings/notifications", { headers: getAuthHeaders() })
      .then(({ data }) => {
        const n = data.notifications;
        if (cancelled || !n) return;
        setCustomerOutboundChannel(n.customerOutboundChannel || "email");
        setCrewOutboundChannel(n.crewOutboundChannel || "email");
      })
      .catch(() => {
        if (!cancelled) setMessage({ type: "error", text: "Could not load notification channels." });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const { data } = await axios.patch(
        "/site-settings/notifications",
        { customerOutboundChannel, crewOutboundChannel },
        { headers: getAuthHeaders() }
      );
      const n = data.notifications;
      if (n) {
        setCustomerOutboundChannel(n.customerOutboundChannel);
        setCrewOutboundChannel(n.crewOutboundChannel);
      }
      setMessage({ type: "success", text: "Saved outbound notification preferences." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Save failed.",
      });
    } finally {
      setSaving(false);
    }
  }, [customerOutboundChannel, crewOutboundChannel]);

  const smsChosen =
    customerOutboundChannel === "sms" ||
    customerOutboundChannel === "both" ||
    crewOutboundChannel === "sms" ||
    crewOutboundChannel === "both";

  return (
    <Box
      sx={{
        ...glassPanel,
        borderRadius: 3,
        p: 2.5,
        mb: 3,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Customer & crew alerts
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
        Choose how your organization reaches <strong>customers</strong> and <strong>crews</strong> for job-related
        messages. Email uses your server SMTP; SMS requires a provider (not configured yet).
      </Typography>
      {smsChosen ? (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          SMS delivery is not wired yet — preferences are saved for when Twilio or similar is added.
        </Alert>
      ) : null}
      {message.text ? (
        <Alert severity={message.type === "success" ? "success" : "error"} sx={{ mb: 2, borderRadius: 2 }}>
          {message.text}
        </Alert>
      ) : null}
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
            <FormControl sx={{ minWidth: 200, ...glassTextField }} size="small">
              <InputLabel id="cust-ch-label">Customers</InputLabel>
              <Select
                labelId="cust-ch-label"
                label="Customers"
                value={customerOutboundChannel}
                onChange={(e) => setCustomerOutboundChannel(e.target.value)}
              >
                {OUTBOUND_CHANNEL_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 200, ...glassTextField }} size="small">
              <InputLabel id="crew-ch-label">Crews</InputLabel>
              <Select
                labelId="crew-ch-label"
                label="Crews"
                value={crewOutboundChannel}
                onChange={(e) => setCrewOutboundChannel(e.target.value)}
              >
                {OUTBOUND_CHANNEL_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Button variant="contained" color="primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save channels"}
          </Button>
        </>
      )}
    </Box>
  );
}

function JobsNotificationsPanel({ onInboxSaved }) {
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
        Configure the <strong>Fast quote</strong> inbox and how job-related messages should reach customers and
        field crews (email, SMS, or both when supported).
      </Typography>
      <QuoteNotificationSettings onSaved={onInboxSaved} />
      <Divider sx={{ my: 2, opacity: 0.5 }} />
      <OutboundNotificationChannels />
    </>
  );
}

function AdminJobsAndQuoting() {
  const [sub, setSub] = useState(JOBS_SUB_SERVICE);
  const [leadStripVersion, setLeadStripVersion] = useState(0);

  return (
    <>
      <Typography variant="h5" fontWeight={700} letterSpacing="-0.02em" gutterBottom>
        Jobs & quoting
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
        Manage scheduled work and inbound <strong>Fast quote</strong> leads; open <strong>Notifications</strong> for
        inbox email and outbound channels.
      </Typography>
      <Box
        sx={{
          ...glassPanelStrong,
          borderRadius: 2,
          px: 0.5,
          pt: 0.5,
          mb: 2,
          border: "1px solid rgba(255,255,255,0.55)",
        }}
      >
        <Tabs
          value={sub}
          onChange={(_, v) => setSub(v)}
          variant="fullWidth"
          sx={{
            minHeight: 48,
            "& .MuiTab-root": {
              minHeight: 48,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.9rem",
            },
          }}
        >
          <Tab label="Service jobs" value={JOBS_SUB_SERVICE} />
          <Tab label="Notifications" value={JOBS_SUB_NOTIFICATIONS} />
        </Tabs>
      </Box>
      {sub === JOBS_SUB_SERVICE ? (
        <>
          <ServiceJobsLeadSummary
            onConfigureNotifications={() => setSub(JOBS_SUB_NOTIFICATIONS)}
            refreshKey={leadStripVersion}
          />
          <AdminJobsGrid />
        </>
      ) : (
        <JobsNotificationsPanel onInboxSaved={() => setLeadStripVersion((v) => v + 1)} />
      )}
    </>
  );
}

function QuoteNotificationSettings({ onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get("/user/settings", { headers: getAuthHeaders() });
        if (!cancelled) {
          setEmail(data.quoteNotificationEmail || "");
        }
      } catch {
        if (!cancelled) {
          setMessage({ type: "error", text: "Could not load settings. Try signing in again." });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      await axios.patch(
        "/user/settings",
        { quoteNotificationEmail: email },
        { headers: getAuthHeaders() }
      );
      setMessage({
        type: "success",
        text: "Saved. Fast quote submissions from the quote page will be emailed to this address.",
      });
      if (typeof onSaved === "function") onSaved();
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Save failed.",
      });
    } finally {
      setSaving(false);
    }
  }, [email, onSaved]);

  return (
    <Box
      sx={{
        ...glassPanel,
        borderRadius: 3,
        p: 2.5,
        mb: 3,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Quote notifications
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
        Set the inbox that receives <strong>Fast quote</strong> requests from the public quote page (customer
        contact, properties, and selected services). Only administrator accounts can change this.
      </Typography>
      {message.text ? (
        <Alert severity={message.type === "success" ? "success" : "error"} sx={{ mb: 2, borderRadius: 2 }}>
          {message.text}
        </Alert>
      ) : null}
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      ) : (
        <>
          <TextField
            label="Notification email"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="office@yourbusiness.com"
            sx={{ mb: 2, ...glassTextField }}
            helperText="SMTP must be configured on the server for emails to send."
          />
          <Button variant="contained" color="primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save notification address"}
          </Button>
        </>
      )}
    </Box>
  );
}

function HomeHeroSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [headline, setHeadline] = useState("");
  const [subheadline, setSubheadline] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    let cancelled = false;
    axios
      .get("/site-settings/home-hero")
      .then(({ data }) => {
        const h = data.homeHero;
        if (cancelled || !h) return;
        setHeadline(typeof h.headline === "string" ? h.headline : "");
        setSubheadline(typeof h.subheadline === "string" ? h.subheadline : "");
        setImageUrl(typeof h.imageUrl === "string" ? h.imageUrl : "");
      })
      .catch(() => {
        if (!cancelled) {
          setMessage({ type: "error", text: "Could not load home hero settings." });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      await axios.patch(
        "/site-settings/home-hero",
        {
          headline: headline.trim(),
          subheadline: subheadline.trim(),
          imageUrl: imageUrl.trim(),
        },
        { headers: getAuthHeaders() }
      );
      setMessage({
        type: "success",
        text: "Saved. Refresh the quote page to see the hero headline, supporting copy, and background image.",
      });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Save failed.",
      });
    } finally {
      setSaving(false);
    }
  }, [headline, subheadline, imageUrl]);

  return (
    <Box
      sx={{
        ...glassPanel,
        borderRadius: 3,
        p: 2.5,
        mb: 3,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Quote page hero (first section)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
        This is the top banner visitors see <strong>before</strong> they pick an address — headline, supporting
        text, and full-width background image (same area as the quote flow hero).
      </Typography>
      {message.text ? (
        <Alert severity={message.type === "success" ? "success" : "error"} sx={{ mb: 2, borderRadius: 2 }}>
          {message.text}
        </Alert>
      ) : null}
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      ) : (
        <>
          <TextField
            label="Headline"
            required
            fullWidth
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            sx={{ mb: 2, ...glassTextField }}
            helperText="Main call to action title (glass card on the hero)."
          />
          <TextField
            label="Supporting copy"
            fullWidth
            multiline
            minRows={3}
            value={subheadline}
            onChange={(e) => setSubheadline(e.target.value)}
            sx={{ mb: 2, ...glassTextField }}
            helperText="Secondary line under the headline — explain the offer or next step."
          />
          <TextField
            label="Background image URL"
            fullWidth
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            sx={{ mb: 2, ...glassTextField }}
            helperText="Full URL (https://…) or site path (e.g. /logo192.png). Leave empty to keep the default bundled hero image."
          />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
            <Button variant="contained" color="primary" onClick={save} disabled={saving || !headline.trim()}>
              {saving ? "Saving…" : "Save hero"}
            </Button>
            <Button component={RouterLink} to="/quote" target="_blank" rel="noopener noreferrer" variant="outlined">
              Preview quote page
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

const JOB_STATUSES = ["requested", "scheduled", "in_progress", "completed", "cancelled"];

function AccountCard({ variant }) {
  const isAdmin = variant === "admin";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });

  const load = useCallback(async () => {
    setMessage({ type: "", text: "" });
    try {
      const { data } = await axios.get("/user/settings", { headers: getAuthHeaders() });
      setFirstname(data.firstname || "");
      setLastname(data.lastname || "");
      setPhone(data.phone || "");
      setAddress(data.address || "");
      setEmail(data.email || "");
    } catch {
      setMessage({ type: "error", text: "Could not load account. Try signing in again." });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get("/user/settings", { headers: getAuthHeaders() });
        if (!cancelled) {
          setFirstname(data.firstname || "");
          setLastname(data.lastname || "");
          setPhone(data.phone || "");
          setAddress(data.address || "");
          setEmail(data.email || "");
        }
      } catch {
        if (!cancelled) {
          setMessage({ type: "error", text: "Could not load account. Try signing in again." });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      await axios.patch(
        "/user/profile",
        { firstname, lastname, phone, address },
        { headers: getAuthHeaders() }
      );
      await load();
      setMessage({
        type: "success",
        text: isAdmin
          ? "Contact details saved. These stay on file for your team."
          : "Profile saved. We use this phone and service address when scheduling visits.",
      });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Save failed.",
      });
    } finally {
      setSaving(false);
    }
  }, [firstname, lastname, phone, address, load, isAdmin]);

  if (loading) {
    return (
      <Box sx={{ ...glassPanelStrong, borderRadius: 3, p: 2.5, mb: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...glassPanelStrong,
        borderRadius: 3,
        p: 2.5,
        mb: 3,
      }}
    >
      <Typography variant="overline" color="text.secondary">
        {isAdmin ? "Administrator account" : "Your profile & service location"}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2, lineHeight: 1.5 }}>
        {isAdmin
          ? "Your sign-in email is fixed. Update your name, phone, and business or dispatch address below."
          : "Keep your contact info and the property we service up to date. Your email is your login and cannot be changed here."}
      </Typography>
      {message.text ? (
        <Alert severity={message.type === "success" ? "success" : "error"} sx={{ mb: 2, borderRadius: 2 }}>
          {message.text}
        </Alert>
      ) : null}
      <TextField
        label="Email (login)"
        fullWidth
        value={email}
        disabled
        sx={{ mb: 2, ...glassTextField }}
      />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <TextField
          label="First name"
          required
          value={firstname}
          onChange={(e) => setFirstname(e.target.value)}
          sx={{ flex: "1 1 160px", minWidth: 140, ...glassTextField }}
        />
        <TextField
          label="Last name"
          value={lastname}
          onChange={(e) => setLastname(e.target.value)}
          sx={{ flex: "1 1 160px", minWidth: 140, ...glassTextField }}
        />
      </Box>
      <TextField
        label="Phone"
        type="tel"
        fullWidth
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="(555) 123-4567"
        sx={{ mb: 2, ...glassTextField }}
        helperText={isAdmin ? "Reachable number for crews or customers." : "Best number to reach you about visits."}
      />
      <TextField
        label={isAdmin ? "Business / dispatch address" : "Service property address"}
        fullWidth
        multiline
        minRows={2}
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        placeholder="Street, city, state, ZIP"
        sx={{ mb: 2, ...glassTextField }}
      />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
        <Button variant="contained" color="primary" onClick={save} disabled={saving || !firstname.trim()}>
          {saving ? "Saving…" : "Save profile"}
        </Button>
        <LogOutButton />
      </Box>
    </Box>
  );
}

function RequestServiceForm({ onCreated }) {
  const { services: catalogServices, loading: catalogLoading } = useCatalogServices();
  const [loadingDefaults, setLoadingDefaults] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [serviceId, setServiceId] = useState("");
  const [propertyAddress, setPropertyAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    if (!catalogServices.length) return;
    if (!serviceId || !catalogServices.some((s) => s.id === serviceId)) {
      setServiceId(catalogServices[0].id);
    }
  }, [catalogServices, serviceId]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get("/user/settings", { headers: getAuthHeaders() })
      .then(({ data }) => {
        if (!cancelled) setPropertyAddress(data.address || "");
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingDefaults(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = catalogServices.find((s) => s.id === serviceId) || catalogServices[0];

  const submit = useCallback(async () => {
    if (!selected) return;
    setSubmitting(true);
    setMessage({ type: "", text: "" });
    try {
      await axios.post(
        "/jobs",
        {
          serviceName: selected.name,
          servicePrice: selected.price,
          propertyAddress: propertyAddress.trim(),
          notes: notes.trim(),
          scheduledDate: scheduledDate.trim(),
        },
        { headers: getAuthHeaders() }
      );
      setMessage({ type: "success", text: "Request submitted. It will appear in your services list below." });
      setNotes("");
      setScheduledDate("");
      if (onCreated) onCreated();
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not submit request.",
      });
    } finally {
      setSubmitting(false);
    }
  }, [selected, propertyAddress, notes, scheduledDate, onCreated]);

  return (
    <Box
      sx={{
        ...glassPanel,
        borderRadius: 3,
        p: 2.5,
        mb: 3,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Request a service
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
        Services match the <strong>quote page</strong> catalog. Your provider sees the request in the admin job list.
      </Typography>
      {message.text ? (
        <Alert severity={message.type === "success" ? "success" : "error"} sx={{ mb: 2, borderRadius: 2 }}>
          {message.text}
        </Alert>
      ) : null}
      <FormControl fullWidth sx={{ mb: 2 }} disabled={loadingDefaults || catalogLoading || !catalogServices.length}>
        <InputLabel id="service-select-label">Service</InputLabel>
        <Select
          labelId="service-select-label"
          label="Service"
          value={serviceId}
          onChange={(e) => setServiceId(e.target.value)}
        >
          {catalogServices.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name} — {s.price}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="Property address for this job"
        fullWidth
        multiline
        minRows={2}
        value={propertyAddress}
        onChange={(e) => setPropertyAddress(e.target.value)}
        sx={{ mb: 2, ...glassTextField }}
        helperText="Defaults from your saved profile; you can change it per request."
      />
      <TextField
        label="Preferred date (optional)"
        type="date"
        fullWidth
        InputLabelProps={{ shrink: true }}
        value={scheduledDate}
        onChange={(e) => setScheduledDate(e.target.value)}
        sx={{ mb: 2, ...glassTextField }}
      />
      <TextField
        label="Notes (optional)"
        fullWidth
        multiline
        minRows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        sx={{ mb: 2, ...glassTextField }}
      />
      <Button
        variant="contained"
        onClick={submit}
        disabled={submitting || loadingDefaults || catalogLoading || !selected}
      >
        {submitting ? "Submitting…" : "Submit request"}
      </Button>
    </Box>
  );
}

function formatShortDate(value) {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}

const customerJobColumns = [
  { field: "serviceName", headerName: "Service", flex: 1, minWidth: 120 },
  { field: "servicePrice", headerName: "Price", width: 100 },
  { field: "propertyAddress", headerName: "Address", flex: 1, minWidth: 140 },
  { field: "status", headerName: "Status", width: 120 },
  {
    field: "scheduledDate",
    headerName: "Preferred",
    width: 110,
    valueFormatter: (params) => params.value || "—",
  },
  {
    field: "createdAt",
    headerName: "Requested",
    width: 110,
    valueFormatter: (params) => formatShortDate(params.value),
  },
];

function CustomerJobsGrid({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    axios
      .get("/jobs", { headers: getAuthHeaders() })
      .then(({ data }) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load your services. Try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  return (
    <Box
      sx={{
        ...glassPanel,
        borderRadius: 3,
        p: 2,
        mb: 3,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, px: 0.5 }}>
        Your services
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 0.5, lineHeight: 1.5 }}>
        Requests you submit and their status are stored in the database and shown here.
      </Typography>
      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 3 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            height: Math.max(280, Math.min(420, 80 + rows.length * 52)),
            width: "100%",
            minHeight: 280,
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid rgba(255,255,255,0.6)",
            backgroundColor: "rgba(255,255,255,0.35)",
            backdropFilter: "blur(12px)",
          }}
        >
          <DataGrid
            rows={rows}
            columns={customerJobColumns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            disableSelectionOnClick
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "rgba(255,255,255,0.5)",
                backdropFilter: "blur(8px)",
              },
              "& .MuiDataGrid-cell": { borderColor: "rgba(0,0,0,0.06)" },
            }}
          />
        </Box>
      )}
    </Box>
  );
}

function AdminJobsGrid() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusError, setStatusError] = useState("");

  const handleStatusChange = useCallback(async (rowId, status) => {
    setStatusError("");
    try {
      await axios.patch(`/jobs/${rowId}`, { status }, { headers: getAuthHeaders() });
      setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, status } : r)));
    } catch (err) {
      setStatusError(err.response?.data?.message || "Could not update status.");
    }
  }, []);

  const adminColumns = useMemo(
    () => [
      { field: "customerName", headerName: "Customer", width: 140 },
      { field: "customerEmail", headerName: "Email", flex: 1, minWidth: 160 },
      { field: "customerPhone", headerName: "Phone", width: 120 },
      { field: "serviceName", headerName: "Service", width: 120 },
      { field: "propertyAddress", headerName: "Address", flex: 1, minWidth: 140 },
      {
        field: "status",
        headerName: "Status",
        width: 168,
        sortable: false,
        renderCell: (params) => (
          <FormControl size="small" fullWidth sx={{ my: 0.5 }} onClick={(e) => e.stopPropagation()}>
            <Select
              value={params.row.status}
              onChange={(e) => handleStatusChange(params.row.id, e.target.value)}
              displayEmpty
            >
              {JOB_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s.replace(/_/g, " ")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
      },
      {
        field: "createdAt",
        headerName: "Requested",
        width: 110,
        valueFormatter: (params) => formatShortDate(params.value),
      },
    ],
    [handleStatusChange]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    axios
      .get("/jobs", { headers: getAuthHeaders() })
      .then(({ data }) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load jobs.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box
      sx={{
        ...glassPanel,
        borderRadius: 3,
        p: 2,
        mb: 3,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, px: 0.5 }}>
        All service jobs
      </Typography>
      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}
      {statusError ? (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setStatusError("")}>
          {statusError}
        </Alert>
      ) : null}
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 3 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            height: 440,
            width: "100%",
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid rgba(255,255,255,0.6)",
            backgroundColor: "rgba(255,255,255,0.35)",
            backdropFilter: "blur(12px)",
          }}
        >
          <DataGrid
            rows={rows}
            columns={adminColumns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            disableSelectionOnClick
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "rgba(255,255,255,0.5)",
                backdropFilter: "blur(8px)",
              },
              "& .MuiDataGrid-cell": { borderColor: "rgba(0,0,0,0.06)" },
            }}
          />
        </Box>
      )}
      <Reports />
    </Box>
  );
}

function Reports() {
  return (
    <Box sx={{ mt: 2 }}>
      <Card
        sx={{
          borderRadius: 3,
          backgroundColor: "rgba(255,255,255,0.4)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255,255,255,0.65)",
        }}
      >
        <CardActionArea sx={{ p: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            Reports
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Summary and export (placeholder)
          </Typography>
        </CardActionArea>
      </Card>
    </Box>
  );
}
